import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/Button';
import {
  LayoutDashboard,
  Users,
  UserCog,
  Store,
  Bolt,
  LogOut,
  Menu,
  X,
  Key,
  FolderKey,
  Loader,
  UserPen,
  CalendarClock
} from 'lucide-react';
import CambiarContra from '../modals/CambiarContra'; 
import EditarPerfil from '../modals/EditarPerfil';

export function Layout() {
  const [openCambiar, setOpenCambiar] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const { user, logout, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false); // Cerrado por defecto en móvil
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  //console.log("EL USUARIO ES: ", user)

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  // Detectar cambios de tamaño de ventana
  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Cerrar sidebar en móvil al cambiar tamaño
      if (mobile) setSidebarOpen(false);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const adminLinks = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/usuarios', icon: Users, label: 'Usuarios' },
    { path: '/empleados', icon: UserCog, label: 'Empleados' },
    { path: '/agentes', icon: Store, label: 'Agentes' },
    { path: '/otros', icon: Bolt, label: 'Otros' },
    { path: '/tokens', icon: FolderKey, label: 'Tokens' },
  ];

  const operadorLinks = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Cierre del Día' },
    { path: '/cierreanterior', icon: CalendarClock, label: 'Cierres Anteriores' },
  ];

  const links = user?.idRol === 1 ? adminLinks : operadorLinks;

  const handleNavClick = () => {
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div className="h-screen bg-background flex flex-col md:flex-row overflow-hidden">
      {/* Overlay para móvil cuando sidebar está abierto */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:relative top-0 left-0 h-screen z-40 ${
          sidebarOpen ? 'w-64' : 'w-0 md:w-64'
        } bg-[#013d79] text-white transition-all duration-300 overflow-hidden flex flex-col`}
      >
        <div className="p-4 md:p-6 border-b border-white/10 flex-shrink-0">
          <h2 className="text-lg md:text-xl font-bold">Sistema de Agentes</h2>
          <p className="text-xs text-white/50 mt-1">
            {user?.role === 'admin' ? 'Administrador' : 'Operador'}
          </p>
        </div>

        <nav className="flex-1 p-3 md:p-4 space-y-1 md:space-y-2 overflow-y-auto scrollbar-hide">
          {links.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.path}
                to={link.path}
                onClick={handleNavClick}
                className={`flex items-center gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg transition-colors text-sm md:text-base ${
                  isActive(link.path)
                    ? 'bg-accent text-white'
                    : 'hover:bg-white/10'
                }`}
              >
                <Icon className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                <span className="truncate">{link.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-3 md:p-4 border-t border-white/10 space-y-1 md:space-y-2 flex-shrink-0">
        <button
          onClick={() => setOpenCambiar(true)}
          className="w-full flex items-center gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg hover:bg-white/10 transition-colors text-left text-sm md:text-base"
        >
          <Key className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
          <span className="truncate">Cambiar Contraseña</span>
        </button>

        <button
          onClick={() => setOpenEditar(true)}
          className="w-full flex items-center gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg hover:bg-white/10 transition-colors text-left text-sm md:text-base"
        >
          <UserPen className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
          <span className="truncate">Editar Perfil</span>
        </button>


          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 md:px-4 py-2 md:py-3 rounded-lg hover:bg-white/10 transition-colors text-left text-sm md:text-base"
          >
            <LogOut className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
            <span className="truncate">Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col w-full overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-border px-4 md:px-6 py-3 md:py-4 flex items-center justify-between sticky top-0 z-20">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-accent/10 rounded-lg transition-colors md:p-2"
            aria-label="Toggle menu"
          >
            {sidebarOpen && !isMobile ? (
              <X className="w-5 h-5 md:w-6 md:h-6 text-foreground" />
            ) : (
              <Menu className="w-5 h-5 md:w-6 md:h-6 text-foreground" />
            )}
          </button>
          <div className="flex items-center gap-2 md:gap-4">
            <span className="text-xs md:text-sm text-muted-foreground hidden sm:inline">
              {new Date().toLocaleDateString('es-GT', {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </span>
            <span className="text-xs md:text-sm text-muted-foreground sm:hidden">
              {new Date().toLocaleDateString('es-GT', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-3 md:p-6 overflow-auto">
          <CambiarContra
            isOpen={openCambiar}
            onClose={() => setOpenCambiar(false)}
            onGuardado={() => setOpenCambiar(false)}
          />
          <EditarPerfil
            isOpen={openEditar}
            onClose={() => setOpenEditar(false)}
            onGuardado={() => setOpenEditar(false)}
            idEditando = {Number(user?.idEmpleado)}
          />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
